import {
  ChangeDetectionStrategy,
  Component,
  input,
  OnInit,
  output,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  CheckboxWithLabelComponent,
  FormControlComponent,
  PrimengButtonDirective,
  PrimengSelectDirective,
  SelectItem,
} from '@web/shared/ui';
import { Guid, Nillable, valueOrDefault } from '@web/utils';
import { WarehouseOwnerDataSourceService } from '../../services/warehouse-owner-data-source.service';
import { Observable } from 'rxjs';
import {
  QuantityUnit,
  QuantityUnitProviderService,
} from '@web/shared/module/measurement';
import { WarehouseProductTypeProviderService } from '../../providers/warehouse-product-type-provider.service';
import { WarehouseProductType } from '../../enums/warehouse-product-type';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { AdminBrandSelectItemsProviderService } from '@web/shared/module/brand';
import {
  WarehouseProductControlsToDisable,
  WarehouseProductFormData,
  WarehouseProductFormGroup,
} from '../../models/warehouse-product-form-data';
import { InputTextModule } from 'primeng/inputtext';
import { Button } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { WarehouseLocalizationDataSourceService } from '../../services/warehouse-localization-data-source.service';
import { Select } from 'primeng/select';

@Component({
  selector: 'app-warehouse-product-form',
  imports: [
    AutoCompleteModule,
    Button,
    CheckboxModule,
    CheckboxWithLabelComponent,
    CommonModule,
    FormControlComponent,
    InputTextModule,
    PrimengButtonDirective,
    PrimengSelectDirective,
    ReactiveFormsModule,
    Select,
  ],
  templateUrl: './warehouse-product-form.component.html',
  styleUrl: './warehouse-product-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WarehouseProductFormComponent implements OnInit {
  /* prettier-ignore */
  private readonly brandDataSource = inject(AdminBrandSelectItemsProviderService);
  private readonly builder = inject(FormBuilder);
  /* prettier-ignore */
  private readonly productTypeProvider = inject(WarehouseProductTypeProviderService);
  private readonly unitProvider = inject(QuantityUnitProviderService);
  /* prettier-ignore */
  private readonly warehouseLocalizationDataSource = inject(WarehouseLocalizationDataSourceService);
  /* prettier-ignore */
  private readonly warehouseOwnerDataSource = inject(WarehouseOwnerDataSourceService);

  /* prettier-ignore */
  public readonly disabledControls = input<WarehouseProductControlsToDisable[]>([]);
  public readonly data = input<Nillable<WarehouseProductFormData>>();
  public readonly submitSuccessEvent = output<WarehouseProductFormData>();

  protected readonly form = this.buildForm();
  protected readonly ownerSelectItems$ = this.ownerSelectItems();
  protected readonly localizationSelectItems$ = this.localizationSelectItems();
  protected readonly brandSelectItems$ = this.brandSelectItems();
  protected readonly unitSelectItems$ = this.unitSelectItems();
  protected readonly productTypeSelectItems$ = this.productTypeSelectItems();

  protected readonly valueOrDefault = valueOrDefault;

  public ngOnInit(): void {
    this.fillByData();
    this.disableControls();
  }

  public handleSubmitClick(): void {
    const data = this.form.getRawValue();

    this.submitSuccessEvent.emit(
      new WarehouseProductFormData({
        brandId: data.brandId,
        durabilityInformation: data.durabilityInformation,
        localizationId: data.localizationId,
        measure: data.measure,
        name: data.name,
        ownerId: data.ownerId,
        promotion: data.promotion,
        quantity: data.quantity,
        sku: data.sku,
        type: data.type,
        unit: data.unit,
      }),
    );
  }

  private brandSelectItems(): Observable<SelectItem<Guid>[]> {
    return this.brandDataSource.fetchCollectionAsSelectItems('id');
  }

  private localizationSelectItems(): Observable<SelectItem<Guid>[]> {
    return this.warehouseLocalizationDataSource.fetchCollectionAsSelectItems();
  }

  private buildForm(): WarehouseProductFormGroup {
    /* prettier-ignore */
    return this.builder.group({
      brandId: this.builder.control<Nillable<Guid>>(null, [Validators.required]),
      durabilityInformation: this.builder.control<Nillable<string>>(null, [ Validators.maxLength(WarehouseProductFormData.MaxDurabilityInformationLength)]),
      localizationId: this.builder.control<Nillable<Guid>>(null),
      measure: this.builder.control<Nillable<string>>(null),
      name: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(WarehouseProductFormData.MaxNameLength)]),
      ownerId: this.builder.control<Nillable<Guid>>(null, [Validators.required]),
      promotion: this.builder.control<Nillable<boolean>>(false),
      quantity: this.builder.control<Nillable<number>>(null, [Validators.required, Validators.min(WarehouseProductFormData.MinQuantity)]),
      sku: this.builder.control<Nillable<string>>(null, [Validators.required, Validators.maxLength(WarehouseProductFormData.MaxSkuLength)]),
      type: this.builder.control<Nillable<WarehouseProductType>>(WarehouseProductType.Cosmetic, [Validators.required]),
      unit: this.builder.nonNullable.control(QuantityUnit.Piece, [Validators.required]),
    })
  }

  private ownerSelectItems(): Observable<SelectItem<Guid>[]> {
    return this.warehouseOwnerDataSource.fetchCollectionAsSelectItems();
  }

  private unitSelectItems(): Observable<SelectItem<Guid>[]> {
    return this.unitProvider.selectItems();
  }

  /* prettier-ignore */
  private productTypeSelectItems(): Observable<SelectItem<WarehouseProductType>[]> {
    return this.productTypeProvider.selectItems();
  }

  private fillByData(): void {
    const data = this.data();

    this.form.patchValue(
      {
        brandId: data?.brandId,
        durabilityInformation: data?.durabilityInformation,
        localizationId: data?.localizationId,
        measure: data?.measure,
        name: data?.name,
        ownerId: data?.ownerId,
        promotion: data?.promotion,
        quantity: data?.quantity,
        sku: data?.sku,
        type: data?.type,
        unit: data?.unit,
      },
      { emitEvent: false },
    );
  }

  private disableControls(): void {
    if (this.shouldBeDisabled('quantity')) {
      this.form.controls.quantity.disable();
    }
  }

  private shouldBeDisabled(key: WarehouseProductControlsToDisable): boolean {
    return this.disabledControls().includes(key);
  }
}
